/**
 * i18next initialization (spec 91 — Wave 0-B web infra).
 *
 * Loading strategy:
 *   - English is BUNDLED EAGERLY. The two en namespaces are static-imported and
 *     registered as `resources`, so they live in the main chunk — English never
 *     flashes translation keys and needs no network round-trip.
 *   - Every OTHER language (es today) is LAZY. `resourcesToBackend` maps a
 *     (lng, ns) pair to a Vite dynamic `import()` of the matching JSON, so each
 *     es namespace becomes its own on-demand chunk, fetched only after Spanish
 *     is first selected (and cached thereafter). `partialBundledLanguages: true`
 *     lets the eager en resources and the lazy backend coexist.
 *
 * Default-locale guarantee (spec §7 prohibition, D2):
 *   - There is NO browser-language detector. The initial language is the
 *     persisted `localStorage['swiftops.locale']` ONLY when it is exactly 'en'
 *     or 'es'; anything else (unset, tampered, unknown) falls back to 'en'.
 *   - With clean localStorage the initial lng is ALWAYS 'en'. Keep this path
 *     trivially auditable — there is no frontend test runner.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

// English bundled eagerly (static imports → main chunk).
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enDashboard from '../locales/en/dashboard.json';
import enWorkOrders from '../locales/en/workOrders.json';
import enInspections from '../locales/en/inspections.json';
// Additional namespaces (spec 91 P3 — full-app sweep). EN is bundled eagerly
// like the core namespaces so the default language never flashes raw keys;
// ES lazy-loads per namespace via resourcesToBackend below.
import enProperties from '../locales/en/properties.json';
import enVendors from '../locales/en/vendors.json';
import enTeam from '../locales/en/team.json';
import enEstimates from '../locales/en/estimates.json';
import enInvoices from '../locales/en/invoices.json';
import enOwners from '../locales/en/owners.json';
import enSurveys from '../locales/en/surveys.json';
import enKnowledge from '../locales/en/knowledge.json';
import enAnalytics from '../locales/en/analytics.json';
import enCalendar from '../locales/en/calendar.json';
import enSettings from '../locales/en/settings.json';
import enOnCall from '../locales/en/onCall.json';
// Wave 2 — giant pages + remaining long-tail (each its own namespace).
import enWorkOrderDetail from '../locales/en/workOrderDetail.json';
import enInspectionWalk from '../locales/en/inspectionWalk.json';
import enAppfolio from '../locales/en/appfolio.json';
import enYardi from '../locales/en/yardi.json';
import enBuildium from '../locales/en/buildium.json';
import enCompliance from '../locales/en/compliance.json';
import enPropertyDetail from '../locales/en/propertyDetail.json';
import enVendorJob from '../locales/en/vendorJob.json';
import enActionItems from '../locales/en/actionItems.json';
import enInspectionTemplates from '../locales/en/inspectionTemplates.json';
import enRecurringInspections from '../locales/en/recurringInspections.json';
import enAssets from '../locales/en/assets.json';
import enResidents from '../locales/en/residents.json';
import enAiMemory from '../locales/en/aiMemory.json';
// Wave 3 — shared components / cross-cutting widgets.
import enNotifications from '../locales/en/notifications.json';
import enInspectionComponents from '../locales/en/inspectionComponents.json';
import enWidgets from '../locales/en/widgets.json';
// Make-Ready module (spec 99 W1) — list + unit-folder pages.
import enMakeReady from '../locales/en/makeReady.json';
// Accounting's own register — bills still to charge out (2026-08-03).
import enAccounting from '../locales/en/accounting.json';
// My Queue unification (spec 110 W2) — the unified work feed page.
import enMyQueue from '../locales/en/myQueue.json';
// The leasing seat's unit task page — one apartment coming back to market.
import enLeasing from '../locales/en/leasing.json';
// The mailbox Inbox (spec 170) — its own namespace, reached as `inbox:` from the
// nav's `common` translator too.
import enInbox from '../locales/en/inbox.json';
// Spec 179 Phase 2 — the report dialogs and the owner's no-login report page.
import enReports from '../locales/en/reports.json';
import enOwnerReport from '../locales/en/ownerReport.json';

export const LOCALE_STORAGE_KEY = 'swiftops.locale';
/**
 * Set to '1' ONLY when the user explicitly picks a language via the switcher.
 * Distinct from LOCALE_STORAGE_KEY (which also gets written by passive account
 * adoption): account-locale adoption is gated on the ABSENCE of this flag, so a
 * returning/different user on a shared device still adopts their own account
 * locale, while a real explicit pick always wins (spec 91 §6).
 */
export const LOCALE_EXPLICIT_KEY = 'swiftops.localeExplicit';
export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Validate an arbitrary value against the supported list; anything else → 'en'. */
export function normalizeLocale(value: unknown): SupportedLocale {
  return value === 'en' || value === 'es' ? value : 'en';
}

/**
 * Read the persisted locale. Returns 'en' unless a valid 'en'/'es' is stored.
 * A clean localStorage MUST resolve to 'en' (spec D2 — no auto-detection).
 */
export function readInitialLocale(): SupportedLocale {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return 'en';
  }
}

const initialLng: SupportedLocale = readInitialLocale();

void i18n
  .use(
    // Non-en languages resolve their namespaces via a Vite dynamic import →
    // one lazy chunk per (lng, ns). en is never routed here (bundled below).
    resourcesToBackend(
      (lng: string, ns: string) => import(`../locales/${lng}/${ns}.json`),
    ),
  )
  .use(initReactI18next)
  .init({
    // English is eagerly bundled so it's instant and offline-safe.
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        dashboard: enDashboard,
        workOrders: enWorkOrders,
        inspections: enInspections,
        properties: enProperties,
        vendors: enVendors,
        team: enTeam,
        estimates: enEstimates,
        invoices: enInvoices,
        owners: enOwners,
        surveys: enSurveys,
        knowledge: enKnowledge,
        analytics: enAnalytics,
        calendar: enCalendar,
        settings: enSettings,
        onCall: enOnCall,
        workOrderDetail: enWorkOrderDetail,
        inspectionWalk: enInspectionWalk,
        appfolio: enAppfolio,
        yardi: enYardi,
        buildium: enBuildium,
        compliance: enCompliance,
        propertyDetail: enPropertyDetail,
        vendorJob: enVendorJob,
        actionItems: enActionItems,
        inspectionTemplates: enInspectionTemplates,
        recurringInspections: enRecurringInspections,
        assets: enAssets,
        residents: enResidents,
        aiMemory: enAiMemory,
        notifications: enNotifications,
        inspectionComponents: enInspectionComponents,
        widgets: enWidgets,
        makeReady: enMakeReady,
        accounting: enAccounting,
        myQueue: enMyQueue,
        leasing: enLeasing,
        inbox: enInbox,
        reports: enReports,
        ownerReport: enOwnerReport,
      },
    },
    partialBundledLanguages: true,
    lng: initialLng,
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    defaultNS: 'common',
    ns: [
      'common',
      'auth',
      'dashboard',
      'workOrders',
      'inspections',
      'properties',
      'vendors',
      'team',
      'estimates',
      'invoices',
      'owners',
      'surveys',
      'knowledge',
      'analytics',
      'calendar',
      'settings',
      'onCall',
      'workOrderDetail',
      'inspectionWalk',
      'appfolio',
      'yardi',
      'buildium',
      'compliance',
      'propertyDetail',
      'vendorJob',
      'actionItems',
      'inspectionTemplates',
      'recurringInspections',
      'assets',
      'residents',
      'aiMemory',
      'notifications',
      'inspectionComponents',
      'widgets',
      'makeReady',
      'accounting',
      'myQueue',
      'leasing',
      'inbox',
      'reports',
      'ownerReport',
    ],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export default i18n;
